import { SubscriptionType } from '~/App/shared/interfaces/EmailSubscription';
import { Session } from '~/App/shared/interfaces/store/Session';
import { orchestration } from '~/config/public';
import axios from '~/helpers/orchestration/axiosWithAuthHandling';
import { getCookieConsent } from '../cookieConsent';

export type Credentials = {
  email: string;
  password: string;
};

export const login = (credentials: Credentials) =>
  axios.post<Session>(
    `${orchestration.url}/common/auth/login`,
    {
      auth: {
        ...credentials,
        cookieConsent: getCookieConsent()
      }
    },
    {
      withCredentials: true,
      responseType: 'json'
    }
  );

export const logout = (_idToken: string) =>
  axios.post(
    `${orchestration.url}/common/auth/logout`,
    {},
    {
      withCredentials: true,
      responseType: 'json'
    }
  );

export const isAuthenticated = async (_idToken: string) => {
  const { data } = await axios.get<boolean>(
    `${orchestration.url}/common/auth/is-authenticated`,
    {
      withCredentials: true,
      responseType: 'json'
    }
  );

  return data;
};

export type HintCode =
  | `started`
  | `noClient`
  | `userSign`
  | `userCancel`
  | `startFailed`
  | `maintenance`
  | `unauthorized`
  | `internalError`
  | `undefinedError`
  | `certificateErr`
  | `requestTimeout`
  | `invalidParameters`
  | `expiredTransaction`
  | `alreadyInProgress`
  | `unsupportedMediaType`
  | `outstandingTransaction`;

export type BankIdStatus = 'pending' | 'complete' | 'failed';

export type BankIdStatusResponse = {
  completionData: Record<string, string>;
  hintCode: HintCode;
  orderRef: string;
  qrCode: string;
  status: BankIdStatus;
};

export const getBankIdStatus = async <T = BankIdStatusResponse>(
  orderRef: string,
  overrideEndpoint?: string
) => {
  const endpoint = overrideEndpoint
    ? overrideEndpoint
    : `${orchestration.url}/common/auth/bankid/statuses/${orderRef}`;

  return axios.get<T>(endpoint, {
    responseType: 'json'
  });
};

export interface BankIdAuthStatusResponse extends BankIdStatusResponse {
  auth: Session['auth'];
  member: Session['member'];
}

export interface BankIdAuthResponse {
  orderRef: string;
  qrCode: string;
  autoStartToken: string;
}

export const postBankIdAuthStart = (signal: AbortSignal) => {
  return axios.post<BankIdAuthResponse>(
    `${orchestration.url}/common/auth/bankid/auth`,
    {
      responseType: 'json'
    },
    {
      signal
    }
  );
};

export const postBankIdLogin = (orderRef: string) => {
  return axios.post<Session>(
    `${orchestration.url}/common/auth/bankid/login`,
    { orderRef, cookieConsent: getCookieConsent() },
    {
      withCredentials: true,
      responseType: 'json'
    }
  );
};

export const postBankIdCancel = (orderRef: string) => {
  return axios.post(
    `${orchestration.url}/common/auth/bankid/cancel`,
    { orderRef },
    {
      responseType: 'json'
    }
  );
};

export const postBankIdVerify = (orderRef: string, _idToken: string) => {
  return axios.post<Session>(
    `${orchestration.url}/common/auth/bankid/verify`,
    { orderRef },
    {
      withCredentials: true,
      responseType: 'json'
    }
  );
};

export const postBankIdRegister = (
  orderRef: string,
  email: string,
  emailSubscriptionNames: SubscriptionType[] = []
) => {
  return axios.post<Session>(
    `${orchestration.url}/common/auth/bankid/register`,
    {
      orderRef,
      email,
      emailSubscriptionNames,
      cookieConsent: getCookieConsent()
    },
    {
      withCredentials: true,
      responseType: 'json'
    }
  );
};

export const postCheckEmail = (email: string, gRecaptchaResponse: string) => {
  return axios.post<{
    email: boolean;
  }>(
    `${orchestration.url}/common/auth/check-email`,
    { email, gRecaptchaResponse },
    {
      responseType: 'json'
    }
  );
};
